/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type Ref } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { useThemeObserver } from '@atlaskit/tokens';

import { Accessibility } from './icons/accessibility';
import { AppProvider } from './icons/app-provider';
import { Border } from './icons/border';
import { Color } from './icons/color';
import { ContactUs } from './icons/contact-us';
import { Content } from './icons/content';
import { ContentDesign } from './icons/content-design';
import { Design } from './icons/design';
import { Develop } from './icons/develop';
import { Elevation } from './icons/elevation';
import { EslintPlugin } from './icons/eslint-plugin';
import { Figma } from './icons/figma';
import { FocusManagement } from './icons/focus-management';
import { Grid } from './icons/grid';
import { Icons } from './icons/icons';
import { Illustration } from './icons/illustration';
import { InformationArchitecture } from './icons/information-architecture';
import { Internationalization } from './icons/internationalization';
import { Layout } from './icons/layout';
import { Logo } from './icons/logo';
import { Motion } from './icons/motion';
import { ReleasePhases } from './icons/release-phases';
import { ResponsiveDesign } from './icons/responsive-design';
import { Shape } from './icons/shape';
import { Spacing } from './icons/spacing';
import { StorybookAddon } from './icons/storybook-addon';
import { StylelintPlugin } from './icons/stylelint-plugin';
import { Theming } from './icons/theming';
import { Tokens } from './icons/tokens';
import { Typography } from './icons/typography';
import { UIStylingStandard } from './icons/ui-styling-standard';
import { Vision } from './icons/vision';

export const iconMap = {
	vision: Vision,
	develop: Develop,
	'release-phases': ReleasePhases,
	'content-design': ContentDesign,
	design: Design,
	accessibility: Accessibility,
	content: Content,
	tokens: Tokens,
	'responsive-design': ResponsiveDesign,
	'focus-management': FocusManagement,
	'information-architecture': InformationArchitecture,
	internationalization: Internationalization,
	theming: Theming,
	layout: Layout,
	spacing: Spacing,
	color: Color,
	typography: Typography,
	icons: Icons,
	illustration: Illustration,
	elevation: Elevation,
	border: Border,
	shape: Shape,
	motion: Motion,
	grid: Grid,
	logo: Logo,
	figma: Figma,
	appProvider: AppProvider,
	eslintPlugin: EslintPlugin,
	storybookAddon: StorybookAddon,
	stylelintPlugin: StylelintPlugin,
	'ui-styling-standard': UIStylingStandard,
	contactUs: ContactUs,
};

const styles = cssMap({
	rootLight: {
		'--color-fill': 'black',
		'--color-fill-inverse': 'white',
		'--color-gestural-line': 'transparent',
	},
	rootDark: {
		'--color-fill': 'white',
		'--color-fill-inverse': 'black',
		'--color-gestural-line': 'transparent',
	},
	scribbleLight: {
		'--color-gestural-line': 'black',
	},
	scribbleDark: {
		'--color-gestural-line': 'white',
	},
});

export type RichIconName = keyof typeof iconMap;

export type RichIconProps = {
	name: RichIconName;
	shouldShowScribble?: boolean;
};

export const RichIcon: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<RichIconProps> & React.RefAttributes<HTMLElement>
> = forwardRef(({ name, shouldShowScribble = false }: RichIconProps, ref: Ref<HTMLElement>) => {
	const Icon = iconMap[name];
	const { colorMode = 'light' } = useThemeObserver();

	return (
		<span
			ref={ref}
			css={[
				colorMode === 'dark' ? styles.rootDark : styles.rootLight,
				shouldShowScribble && colorMode === 'light' && styles.scribbleLight,
				shouldShowScribble && colorMode === 'dark' && styles.scribbleDark,
			]}
		>
			<Icon />
		</span>
	);
});
